import { useCallback } from 'react';

import { useGlobalAppState } from '../../../../context/GlobalAppState';
import { useCart } from '../../../../hooks';

import type { ReorderEvent, ReorderStep } from '../../machines/Reorder.machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lets a group order participant decide whether to stay in the group order or
 * leave it after trying to move it to another location.
 */
export const useGroupOrderLeaveConfirmation = () => {
  const { reorderMachineRef } = useGlobalAppState();
  const { cart } = useCart();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isGroupOrderParticipant = Boolean(
    cart?.multipartyDetails?.joinCode && !cart.currentUserOrderPermissions.canSubmitOrder,
  );

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const requestGroupOrderLeaveConfirmation = useCallback(
    (params: RequestParams) => {
      const { reorderEvent } = params;

      const reorderState = reorderMachineRef.getSnapshot();
      const groupOrderReorderRequest = reorderState?.context.groupOrderReorderRequest;

      if (groupOrderReorderRequest) {
        return;
      }

      reorderMachineRef.send({
        type: 'REQUEST_GROUP_ORDER_LEAVE_CONFIRMATION',
        reorderEvent,
        originStep: reorderState?.value as ReorderStep,
      });
    },
    [reorderMachineRef],
  );

  // ───────────────────────────────────────────────────────────────────────────

  return {
    isGroupOrderParticipant,
    requestGroupOrderLeaveConfirmation,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type RequestParams = {
  reorderEvent: ReorderEvent;
};
